import { Response } from 'request-dot-js'

import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { sendToApi } from 'api/client'
import { MentionUser } from 'api/clients/MentionClient'
import { Entity } from 'components/BulkPermissions/Types'
import { CancellationReason } from 'components/Paywall/UpgradePlanModal/components/PaymentForm/PaymentForm'
import { BillingFrequency } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import {
  AlmanacFile,
  Auth,
  Doc,
  HomepageDoc,
  MemberProfile,
  NavSortOrder,
  PublishDocSubscriptionList,
  Workspace,
  WorkspaceSamlSettings,
} from 'types'

import { PaymentPlanKey } from './PaymentPlanClient'
import { StripeInvoice, StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { Variables } from 'hooks/queries/useUpdateBrandPlan'

export enum UnfiledArticleOrder {
  LastEditedAtAsc = 'last_edited_at asc nulls first',
  LastEditedAtDesc = 'last_edited_at desc nulls last',
  TitleAsc = 'lower(title)',
  TitleDesc = 'lower(title) desc',
}

export interface MinimalWorkspace {
  accessor_type: 'Channel'
  id: number
  name: string
  users_count: number
}

export interface BlazeOnboarding {
  brand_voice_created: boolean
  blog_post_created: boolean
  content_campaign_created: boolean
  third_party_integration_created: boolean
  brainstorm_created: boolean
  secret_identity_skipped: boolean
}

export interface NextPayment {
  due_date: number
  subtotal: number
  amount: number
}

export interface Invoice {
  id: string
  created: number
  description: string
  amount: number
  invoice_pdf: string
}

export interface PlanDetails {
  plan: string
  plan_key: string
  billing_frequency: BillingFrequency
  next_payment: NextPayment
  card: { last_four: number; brand: string }
  price: string
  subscription_id: string
}

export interface Overage {
  count: number
  limit: number
}

export interface Overages {
  ai_words: Overage
  plagiarism_checks: Overage
}

export interface FetchSubscriptionResponse {
  success: boolean
  message?: string
  trial_days_left: number | null
  plan: PaymentPlanKey
  subscription_items: {
    plan_key: PaymentPlanKey
    price_id: string
    product_id: string
    quantity: number
  }[]
  billing_frequency: BillingFrequency
  overages: Overages
  next_payment?: NextPayment
  card: { last_four: number; brand: string }
  cancel_at: string
  prices: StripePrice[]
  subscription_payment_method: string
  customer_default_payment_method: string
  status: string
  trial_ended: boolean
}

export const WorkspaceClient = {
  getWorkspace: (key: string) => {
    return sendToApi<MinimalWorkspace>(`/w/${key}`)
  },
  acceptWorkspaceRequest: (token: string) => {
    return sendToApi<void>(`/workspace_join_requests/${token}/accept`, { method: 'POST' })
  },
  denyWorkspaceRequest: (token: string) => {
    return sendToApi<void>(`/workspace_join_requests/${token}/deny`, { method: 'DELETE' })
  },
  leave: (key: string) => sendToApi<void>(`/w/${key}/leave`, { method: 'DELETE' }),
  markAsSeen: (key: string) => sendToApi<void>(`/w/${key}/mark_as_seen`, { method: 'POST' }),
  customizeWorkspace: (workspaceId: number, companyName: string) => {
    return sendToApi<{ user: Auth }>(`/w/${workspaceId}/customize_company`, {
      method: 'PUT',
      body: {
        name: companyName,
      },
    })
  },

  updateWorkspaceGithubIntegration: (workspaceId: number, code: string) => {
    return sendToApi<{ user: Auth }>(`/w/${workspaceId}/github_integration`, {
      method: 'PUT',
      body: {
        code,
      },
    })
  },

  getDefaultProject: (workspaceId: number) => {
    return sendToApi<{ project: AlmanacFile | null }>(`/w/${workspaceId}/default_project`)
  },

  getUploadsProject: async (workspaceId: number) => {
    return sendToApi<{ project: AlmanacFile | null }>(`/w/${workspaceId}/uploads_project`)
  },

  getLastUsedProject: (workspaceId: number) => {
    return sendToApi<{ project: AlmanacFile | null }>(`/w/${workspaceId}/last_used_project`)
  },

  getLastUsedLanguage: (workspaceId: number) => {
    return sendToApi<{ language: string | null }>(`/w/${workspaceId}/last_used_language`)
  },

  fetchGithubResourceMetadata: (workspaceId: number, organization?: string, endpoint?: string, resourceId?: string) => {
    return sendToApi(
      `/w/${workspaceId}/github_integration?organization=${organization}&endpoint=${endpoint}&resource_id=${resourceId}`,
    )
  },

  getNavSortOrder: async (workspaceId: number) => {
    return sendToApi<{ preferred_sort: NavSortOrder }>(`/w/${workspaceId}/sort_order`)
  },

  getUsers: (workspaceId: number, withName?: string, items?: number) => {
    const searchParams = new URLSearchParams()

    if (withName) {
      searchParams.set('with_name', withName)
    }

    if (items) {
      searchParams.set('items', items.toString())
    }

    return sendToApi<{ users: MentionUser[] }>(`/w/${workspaceId}/users?${searchParams.toString()}`)
  },

  updateHomepageDoc: (workspaceId: number, docKey: string | null) => {
    return sendToApi<{ homepage_doc: HomepageDoc | null }>(`/w/${workspaceId}/update_homepage_doc`, {
      method: 'PUT',
      body: {
        doc_key: docKey,
      },
    })
  },

  update: (workspaceId: number, snippets_enabled: boolean) => {
    return sendToApi<void>(`/w/${workspaceId}/update`, {
      method: 'PUT',
      body: {
        snippets_enabled,
      },
    })
  },

  completeVirtualMarketerOnboarding: (workspaceId: number) => {
    return sendToApi<void>(`/w/${workspaceId}/update`, {
      method: 'PUT',
      body: {
        is_virtual_marketer_onboarding_completed: true,
      },
    })
  },

  completeSetFreemiumBrandPlan: (workspaceId: number) => {
    return sendToApi<void>(`/w/${workspaceId}/update`, {
      method: 'PUT',
      body: {
        is_ap_freemium_brand_plan_set: true,
      },
    })
  },

  setAutoReloadCredits: (workspaceId: number, shouldAutoReloadCredits: boolean, autoReloadLimit: number | null) => {
    return sendToApi<void>(`/w/${workspaceId}/update`, {
      method: 'PUT',
      body: {
        auto_reload_credits: shouldAutoReloadCredits,
        auto_reload_limit: autoReloadLimit,
      },
    })
  },

  updateFutureFreemiumBrandPlanContents: (workspaceId: number, payload: Variables) => {
    return sendToApi<void>(`/w/${workspaceId}/update`, {
      method: 'PUT',
      body: {
        future_freemium_brand_plan_contents: payload,
      },
    })
  },

  getHomepageDoc: (workspaceId: number) => {
    return sendToApi<{ homepage_doc: HomepageDoc | null }>(`/w/${workspaceId}/get_homepage_doc`)
  },

  getInvitedWorkspace: (invitation_token: string) => {
    return sendToApi<{ company: Workspace }>(`/invited_workspace`, { params: { invitation_token: invitation_token } })
  },

  getEditableArticles: (workspaceId: number, query?: string, excludeChannelId?: number) => {
    const searchParams = new URLSearchParams()

    if (query) {
      searchParams.set('with_title', query)
    }

    if (excludeChannelId) {
      searchParams.set('exclude_channel_id', excludeChannelId.toString())
    }

    return new PaginatedApiClient<{ articles: Doc[] }>(`/w/${workspaceId}/editable_articles?${searchParams.toString()}`)
  },

  getEntities: (key: string) => {
    return sendToApi<{ channels: Entity[]; users: Entity[] }>(`/w/${key}/entities`)
  },

  getSamlSettings: (workspaceKey: string) => {
    return sendToApi<WorkspaceSamlSettings>(`/w/${workspaceKey}/saml_settings`, {
      method: 'GET',
    })
  },

  saveSamlSettings: (workspaceKey: string, settings: WorkspaceSamlSettings) => {
    return sendToApi<void>(`/w/${workspaceKey}/saml_settings/update`, {
      method: 'PUT',
      body: settings,
    })
  },

  createSubscription: (
    workspaceId: number,
    payment_method_id: string | null,
    plan: string,
    members_count: number,
    frequency: string,
    price_id: string,
    trial_period_days?: number,
    coupon_id?: string,
  ) => {
    return sendToApi<{
      success: boolean
      message?: string
      plan_details: PlanDetails
    }>(`/w/${workspaceId}/subscriptions`, {
      method: 'POST',
      body: {
        payment_method_id,
        plan,
        members_count,
        frequency,
        price_id,
        trial_period_days,
        coupon_id,
      },
    })
  },
  pauseSubscription: ({ workspaceId, numberOfMonths }: { workspaceId: number; numberOfMonths: number }) => {
    return sendToApi<{ subscription: any }>(`/w/${workspaceId}/subscriptions/pause`, {
      method: 'PUT',
      body: {
        number_of_months: numberOfMonths,
      },
    })
  },
  unpauseSubscription: ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<{ subscription: any }>(`/w/${workspaceId}/subscriptions/unpause`, {
      method: 'PUT',
    })
  },
  subscriptionCancelationDiscountEligibility: ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<{ cancelation_discount_eligible: boolean }>(
      `/w/${workspaceId}/subscriptions/cancelation_discount_eligibility`,
      {
        method: 'GET',
      },
    )
  },
  applyDeterCancelationCoupon: ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<{ subscription: any }>(`/w/${workspaceId}/subscriptions/apply_deter_cancelation_coupon`, {
      method: 'PUT',
    })
  },
  cancelSubscription: ({
    workspaceId,
    cancellationReason,
    cancellationDescription,
  }: {
    workspaceId: number
    cancellationReason: CancellationReason
    cancellationDescription: string
  }) => {
    return sendToApi<{ success: boolean; message?: string; subscription?: any }>(
      `/w/${workspaceId}/subscriptions/cancel`,
      {
        method: 'DELETE',
        body: {
          cancellation_reason: cancellationReason,
          cancellation_description: cancellationDescription,
        },
      },
    )
  },

  fetchSubscription: (workspaceId: number) => {
    return sendToApi<FetchSubscriptionResponse>(`/w/${workspaceId}/subscriptions/plan`, {
      method: 'GET',
    })
  },
  createSetupIntent: (workspaceId: number) =>
    sendToApi<{ client_secret: string }>(`/w/${workspaceId}/subscriptions/setup_intent`, { method: 'POST' }),

  updatePaymentMethod: (workspaceId: number, payment_method_id: string) => {
    return sendToApi<{ success: boolean; message?: string; card?: { last_four: number; brand: string } }>(
      `/w/${workspaceId}/subscriptions/update_payment_method`,
      {
        method: 'PUT',
        body: {
          payment_method_id,
        },
      },
    )
  },

  updateSubscription: (workspaceId: number, price_id: string, coupon_id?: string) => {
    return sendToApi<{ success: boolean; message?: string; plan_details?: PlanDetails }>(
      `/w/${workspaceId}/subscriptions/update_price`,
      {
        method: 'PUT',
        body: {
          price_id,
          coupon_id,
        },
      },
    )
  },

  sendFailedPaymentNotification: (workspaceId: number) => {
    return sendToApi<{ success: boolean; message?: string }>(
      `/w/${workspaceId}/subscriptions/failed_payment_notification`,
      {
        method: 'GET',
      },
    )
  },

  sendUpgradePlanNotification: (workspaceId: number) => {
    return sendToApi<{ success: boolean; message?: string }>(
      `/w/${workspaceId}/subscriptions/upgrade_plan_notification`,
      {
        method: 'GET',
      },
    )
  },

  purchaseContentCredits: (
    workspaceId: number,
    num_of_batches: number,
    price_id?: string,
    payment_method_id?: string,
  ) => {
    return sendToApi<{ success: boolean; message?: string }>(
      `/w/${workspaceId}/subscriptions/purchase_content_credits`,
      {
        method: 'POST',
        body: { price_id, num_of_batches, payment_method_id },
      },
    )
  },

  getCustomerInvoices(workspaceId: number) {
    return sendToApi<{ customer_invoices: StripeInvoice[] }>(`/w/${workspaceId}/subscriptions/customer_invoices`)
  },

  purchaseWhiteLabel: (workspaceId: number, priceId: string, paymentMethodId?: string) => {
    return sendToApi<void>(`/w/${workspaceId}/subscriptions/purchase_white_label`, {
      method: 'POST',
      body: { price_id: priceId, payment_method_id: paymentMethodId },
    })
  },

  purchaseAdditionalAccount: (
    workspaceId: number,
    priceId: string,
    paymentMethodId: string | undefined,
    externalIntegrationId: number,
  ) => {
    return sendToApi<void>(`/w/${workspaceId}/subscriptions/purchase_additional_account`, {
      method: 'POST',
      body: { price_id: priceId, payment_method_id: paymentMethodId, external_integration_id: externalIntegrationId },
    })
  },

  cancelWhiteLabel: (workspaceId: number) => {
    return sendToApi<void>(`/w/${workspaceId}/subscriptions/cancel_white_label`, {
      method: 'POST',
    })
  },

  seed: (workspaceKey: string | number) => {
    return sendToApi<{ onboarding_file_path: string }>(`/w/${workspaceKey}/seeds`, { method: 'POST' })
  },

  getPaginatedWorkspaceUsers: (workspaceKey: string | number, params: { [key: string]: string } = {}) => {
    const path = `/w/${workspaceKey}/members`
    const searchParams = new URLSearchParams(params)
    return new PaginatedApiClient<Response<MemberProfile[]>>(`${path}?${searchParams.toString()}`)
  },

  getPublishDocWorkspaceSubscriptions: ({ workspaceId }: { workspaceId: number }) => {
    return sendToApi<PublishDocSubscriptionList>(`/w/${workspaceId}/publish_doc_subscriptions`)
  },

  fetchAllFilesCount: async (workspaceKey: string) => {
    return sendToApi<{ all_files_count: number }>(`/w/${workspaceKey}/all_files_count`, { method: 'GET' })
  },

  fetchBlazeOnboarding: async (workspaceKey: string) => {
    return sendToApi<BlazeOnboarding>(`/w/${workspaceKey}/blaze_onboarding`, { method: 'GET' })
  },

  skipThirdPartyIntegration: async (workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/skip_third_party_integration`, { method: 'POST' })
  },

  fetchAdminEmail: async (workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/admin_email`, { method: 'GET' })
  },
}
